import type { DeskDetail, ShareAccess, UpdateDeskShareRequest } from '@deskworks/shared-types';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, Copy, Link2, RefreshCw, Users } from 'lucide-react';
import { useState } from 'react';
import { Field } from '@/components/field';
import { LoadingRows } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { useToast } from '@/lib/toast';
import { ACCESS_TEXT } from '@/routes/shared-desk';

const shareKey = (deskId: string) => ['desk', deskId, 'share'] as const;

const ACCESS_LABEL: Record<ShareAccess, string> = { none: t('No access'), read: ACCESS_TEXT.read.label, write: ACCESS_TEXT.write.label };

/**
 * "Share" on a desk (docs/features/desk-sharing.md): one link, open to anyone who has it (after the form the account set
 * up) and to people with a Deskworks account, each with its own access; optionally a password and an end date.
 */
export function ShareDeskDialog({ desk, open, onOpenChange }: { desk: DeskDetail; open: boolean; onOpenChange: (open: boolean) => void }) {
  const toast = useToast();
  const link = useQuery({ queryKey: shareKey(desk.id), queryFn: () => api.sharing.get(desk.id), enabled: open });
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);
  const save = useApiMutation((body: UpdateDeskShareRequest) => api.sharing.update(desk.id, body), {
    errorTitle: t('The link was not changed'),
    invalidate: [shareKey(desk.id)],
  });
  const renew = useApiMutation(() => api.sharing.renew(desk.id), {
    invalidate: [shareKey(desk.id)],
    success: t('New link made. The old one no longer works.'),
  });

  const l = link.data;
  const url = l ? `${window.location.origin}/s/${l.token}` : '';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('Copy it from the field instead.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('Share “{desk}”', { desk: desk.name })}</DialogTitle>
          <DialogDescription>
            {t('People outside your account open this desk by a link: its views, and if you allow it, their own chats with its Lead. Work they ask for waits for your approval.')}
          </DialogDescription>
        </DialogHeader>
        {link.isPending ? (
          <LoadingRows rows={3} />
        ) : !l ? (
          <Button onClick={() => save.mutate({ enabled: true })} loading={save.isPending}>
            <Link2 /> {t('Create link')}
          </Button>
        ) : (
          <div className="grid gap-4">
            <label className="flex cursor-pointer items-center justify-between gap-3 text-sm">
              <span>
                <span className="font-medium">{t('Shared by link')}</span>
                <span className="block text-xs text-muted-foreground">{l.enabled ? t('The link works.') : t('Off: the link doesn’t open; guests already in stay in.')}</span>
              </span>
              <Switch checked={l.enabled} onCheckedChange={(enabled) => save.mutate({ enabled })} disabled={save.isPending} />
            </label>
            <div className="flex gap-2">
              <Input readOnly value={url} onFocus={(e) => e.currentTarget.select()} className="font-mono text-xs" aria-label={t('Link')} disabled={!l.enabled} />
              <Button variant="outline" onClick={() => void copy()} disabled={!l.enabled}>
                {copied ? <Check /> : <Copy />} {copied ? t('Copied') : t('Copy')}
              </Button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <AccessField
                label={t('Anyone who has the link')}
                hint={t('After filling in their details, without an account.')}
                value={l.anyoneAccess}
                onChange={(anyoneAccess) => save.mutate({ anyoneAccess })}
              />
              <AccessField
                label={t('With a Deskworks account')}
                hint={t('Signed in, or registering through the link.')}
                value={l.registeredAccess}
                onChange={(registeredAccess) => save.mutate({ registeredAccess })}
              />
            </div>
            <Field label={t('Password')} optional hint={l.hasPassword ? t('Set: people need it to open the link.') : t('Asked before anything else.')}>
              <div className="flex gap-2">
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={l.hasPassword ? '••••••••' : ''} autoComplete="new-password" />
                <Button
                  variant="outline"
                  disabled={password.length < 4 || save.isPending}
                  onClick={() => save.mutate({ password }, { onSuccess: () => setPassword('') })}
                >
                  {l.hasPassword ? t('Change') : t('Set')}
                </Button>
                {l.hasPassword && (
                  <Button variant="ghost" onClick={() => save.mutate({ password: null })} disabled={save.isPending}>
                    {t('Remove')}
                  </Button>
                )}
              </div>
            </Field>
            <Field label={t('Works until')} optional hint={t('After that day the link no longer opens.')}>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={l.expiresAt ? l.expiresAt.slice(0, 10) : ''}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => save.mutate({ expiresAt: e.target.value ? new Date(`${e.target.value}T23:59:59`).toISOString() : null })}
                />
                {l.expiresAt && (
                  <Button variant="ghost" onClick={() => save.mutate({ expiresAt: null })}>
                    {t('No end')}
                  </Button>
                )}
              </div>
            </Field>
            <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3 text-sm">
              <Link to="/w/$workspaceId/settings" params={{ workspaceId: desk.workspaceId }} search={{ tab: 'people' }} className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground">
                <Users className="size-4" /> {l.guests === 1 ? t('1 guest') : t('{n} guests', { n: l.guests })}
              </Link>
              <Button
                variant="ghost"
                size="sm"
                loading={renew.isPending}
                onClick={() => window.confirm(t('Make a new link? The current one stops working; guests already in stay in.')) && renew.mutate()}
              >
                <RefreshCw /> {t('New link')}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AccessField({ label, hint, value, onChange }: { label: string; hint: string; value: ShareAccess; onChange: (value: ShareAccess) => void }) {
  return (
    <Field label={label} hint={hint}>
      <Select value={value} onValueChange={(v) => onChange(v as ShareAccess)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(['none', 'read', 'write'] as const).map((a) => (
            <SelectItem key={a} value={a}>
              {ACCESS_LABEL[a]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}
